import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { NumberParam, StringParam, useQueryParams } from 'use-query-params'

import { deleteConversation } from '../../../api/conversations'
import { ReactComponent as Thrash } from '../../../assets/icons/trash.svg'
import { trimIfTooLong } from '../../../utils/text'
import { msToExactDate } from '../../../utils/time'
import { buildParams, DashArrayParam } from '../../../utils/useQueryParams'
import { IConversations } from '../Conversations'

import { ConversationsFiltersEnum } from './ConversationsFilters/ConversationsFilters'
import ConversationsNavHead from './ConversationsNavHead/ConversationsNavHead'

import { joinClassNames } from '../../../utils/styles'
import styles from './conversations-nav.module.scss'

interface IConversationsNavProps {
    conversations: IConversations[]
    setPagination: React.Dispatch<React.SetStateAction<number>>
    className: string
    areAllConversationsFetched: boolean
    refetchConversations: () => void
}

function ConversationsNav(props: IConversationsNavProps) {
    const {
        conversations,
        setPagination,
        className,
        areAllConversationsFetched,
        refetchConversations,
    } = props

    const { conversationId } = useParams()
    const { t, i18n } = useTranslation()
    const navigate = useNavigate()

    const [query] = useQueryParams({
        [ConversationsFiltersEnum.ENTITIES]: DashArrayParam,
        [ConversationsFiltersEnum.INTENTS]: DashArrayParam,
        [ConversationsFiltersEnum.ACTIONS]: DashArrayParam,
        [ConversationsFiltersEnum.DATE_FROM]: StringParam,
        [ConversationsFiltersEnum.DATE_TO]: StringParam,
        [ConversationsFiltersEnum.CONVERSATION_LENGTH]: NumberParam,
    })

    const handleDeleteConversation = async (key: string) => {
        const confirmDelete = confirm(t('delete_conversation_confirm'))
        if (!confirmDelete) return

        await deleteConversation(key)
        refetchConversations()
    }

    return (
        <nav className={joinClassNames(className, styles.conversationsNav)}>
            <ConversationsNavHead />
            <div className={styles.conversationsNav__content}>
                {conversations?.map((conversation) => (
                    <div
                        key={conversation.id}
                        className={styles.conversationsNav__conversationWrapper}
                    >
                        <button
                            className={joinClassNames(
                                styles.conversationsNav__redirectBtn,
                                conversation.id === conversationId &&
                                    styles.conversationsNav__redirectBtnActive
                            )}
                            onClick={() =>
                                navigate({
                                    pathname: `./../${conversation.id}`,
                                    search: buildParams(query),
                                })
                            }
                        >
                            <p className={styles.conversationsNav__message}>
                                {trimIfTooLong(conversation.message || '', 50)}
                            </p>
                            <p className={styles.conversationsNav__date}>
                                {msToExactDate(
                                    conversation.timestamp,
                                    i18n.language
                                )}
                            </p>
                        </button>
                        <button
                            onClick={() =>
                                handleDeleteConversation(conversation.id)
                            }
                            className={styles.conversationsNav__deleteBtn}
                        >
                            <Thrash />
                        </button>
                    </div>
                ))}
                {!areAllConversationsFetched && conversations.length ? (
                    <button
                        className={styles.conversationsNav__loadMoreBtn}
                        onClick={() =>
                            setPagination((pagination) => pagination + 1)
                        }
                    >
                        {t('load_more')}
                    </button>
                ) : null}
            </div>
        </nav>
    )
}

export default ConversationsNav
